import React from 'react';
import { Link } from 'react-router-dom';
import {
  ADMIN_COMPANY_PLANS,
  ADMIN_COMPANY_STATUSES,
  companyNestedLink,
  createCompanyCreateRequest,
  createCompanyDraft,
  createCompanyPatchRequest,
  createNewCompanyDraft,
  formatCascadeCounts,
  isCompanyDraftDirty,
  validateCompanyDraft,
  type CompanyDraft,
  type CompanyDraftErrors,
} from '../lib/adminCompanies';
import { getAdminApiError } from '../lib/adminApiErrors';
import { formatExportElementQuota } from '../lib/adminLicenses';
import { useSuperAdminStore } from '../store/useSuperAdminStore';
import type {
  AdminCompanyCreateRequest,
  AdminCompanyDeleteOptions,
  AdminCompanyDetailDto,
  AdminCompanyDto,
  AdminCompanyPatchRequest,
  AdminCompanyStatus,
  AdminMutationOutcome,
} from '../types';
import { AdminButton, ConfirmDialog, EntityDrawer, OccupancyMeter, StatusPill, useAdminToast } from './components';
import CompanyLegalSummary from './legal/CompanyLegalSummary';
import './styles/adminCompanies.css';

export interface CompanyDrawerProps {
  open: boolean;
  mode: 'create' | 'edit';
  company: AdminCompanyDetailDto | null;
  loading?: boolean;
  onClose: () => void;
  onCreate: (request: AdminCompanyCreateRequest) => Promise<AdminCompanyDetailDto>;
  onSave: (id: number, patch: AdminCompanyPatchRequest) => Promise<AdminCompanyDetailDto>;
  onDelete: (id: number, options: AdminCompanyDeleteOptions) => Promise<AdminMutationOutcome>;
  onChangeStatus: (id: number, status: AdminCompanyStatus) => Promise<AdminCompanyDto>;
}

const titleCase = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

const statusTone = (status: AdminCompanyStatus) => status === 'active'
  ? 'success' as const
  : status === 'suspended' ? 'critical' as const : 'warning' as const;

const fieldError = (error: unknown): Partial<Record<keyof CompanyDraft, string>> => {
  const failure = getAdminApiError(error);
  if (/domain/i.test(failure.code) || /domain/i.test(failure.message)) {
    return { domain: failure.message };
  }
  if (/email/i.test(failure.code) || /email/i.test(failure.message)) {
    return { contactEmail: failure.message };
  }
  return {};
};

const CompanyField: React.FC<{
  disabled: boolean;
  error?: string;
  label: string;
  textarea?: boolean;
  value: string;
  onChange: (value: string) => void;
}> = ({ disabled, error, label, textarea = false, value, onChange }) => {
  const id = React.useId();
  return (
    <label className="admin-company-field" htmlFor={id}>
      <span>{label}</span>
      {textarea ? (
        <textarea id={id} disabled={disabled} value={value} onChange={(event) => onChange(event.target.value)} />
      ) : (
        <input id={id} disabled={disabled} value={value} onChange={(event) => onChange(event.target.value)} />
      )}
      {error && <span className="admin-company-field-error" role="alert">{error}</span>}
    </label>
  );
};

const CompanyForm: React.FC<{
  createdAt?: string;
  disabled: boolean;
  draft: CompanyDraft;
  errors: CompanyDraftErrors;
  setField: <Key extends keyof CompanyDraft>(key: Key, value: CompanyDraft[Key]) => void;
}> = ({ createdAt, disabled, draft, errors, setField }) => (
  <div className="admin-company-form">
    <CompanyField disabled={disabled} error={errors.name} label="Name" value={draft.name} onChange={(value) => setField('name', value)} />
    <CompanyField disabled={disabled} error={errors.domain} label="Domain" value={draft.domain} onChange={(value) => setField('domain', value)} />
    <label className="admin-company-field">
      <span>Plan</span>
      <select disabled={disabled} value={draft.plan} onChange={(event) => setField('plan', event.target.value as CompanyDraft['plan'])}>
        {ADMIN_COMPANY_PLANS.map((plan) => <option key={plan} value={plan}>{plan}</option>)}
      </select>
      {errors.plan && <span className="admin-company-field-error" role="alert">{errors.plan}</span>}
    </label>
    <CompanyField disabled={disabled} error={errors.country} label="Country" value={draft.country} onChange={(value) => setField('country', value)} />
    <CompanyField disabled={disabled} error={errors.contactEmail} label="Contact email" value={draft.contactEmail} onChange={(value) => setField('contactEmail', value)} />
    <label className="admin-company-field">
      <span>Status</span>
      <select disabled={disabled} value={draft.status} onChange={(event) => setField('status', event.target.value as CompanyDraft['status'])}>
        {ADMIN_COMPANY_STATUSES.map((status) => <option key={status} value={status}>{titleCase(status)}</option>)}
      </select>
      {errors.status && <span className="admin-company-field-error" role="alert">{errors.status}</span>}
    </label>
    {createdAt && <div className="admin-company-readonly"><span>Created</span><strong>{new Date(createdAt).toLocaleDateString('en-GB')}</strong></div>}
    <CompanyField disabled={disabled} label="Notes" textarea value={draft.notes} onChange={(value) => setField('notes', value)} />
  </div>
);

const NestedDetail: React.FC<{ company: AdminCompanyDetailDto }> = ({ company }) => (
  <section className="admin-company-nested" aria-label="Nested company records">
    <div className="admin-company-nested__header">
      <h3>Licenses</h3>
      <Link to={companyNestedLink('licenses', company.id)}>Open licenses -&gt;</Link>
    </div>
    {company.licenses.length === 0 ? <p>No licenses belong to this company.</p> : company.licenses.map((license) => (
      <div className="admin-company-nested-row" key={license.id}>
        <span>{license.activationKey}</span>
        <span className={`admin-license-quota${license.remainingExportElements === 0 ? ' admin-license-quota--exhausted' : ''}`}>
          {formatExportElementQuota(license.remainingExportElements)}
        </span>
        <StatusPill tone={license.status === 'active' ? 'success' : 'critical'}>{license.status}</StatusPill>
        <OccupancyMeter used={license.registeredUsersCount} maximum={license.allowedRegistrations} label="License occupancy" />
      </div>
    ))}
    <div className="admin-company-nested__header">
      <h3>Users</h3>
      <Link to={companyNestedLink('users', company.id)}>Open users -&gt;</Link>
    </div>
    {company.users.length === 0 ? <p>No users belong to this company.</p> : company.users.map((user) => (
      <div className="admin-company-nested-row" key={user.id}>
        <span>{user.email ?? user.username}</span>
        <StatusPill tone={user.status === 'active' ? 'success' : user.status === 'blocked' ? 'critical' : 'info'}>{user.status}</StatusPill>
        <StatusPill tone="line">{user.role}</StatusPill>
      </div>
    ))}
  </section>
);

const useCompanyDraft = (open: boolean, mode: CompanyDrawerProps['mode'], company: AdminCompanyDetailDto | null) => {
  const [draft, setDraft] = React.useState<CompanyDraft>(() => createNewCompanyDraft());
  const original = React.useMemo(() => company ? createCompanyDraft(company) : createNewCompanyDraft(), [company]);
  React.useEffect(() => {
    if (open) {
      setDraft(mode === 'edit' && company ? createCompanyDraft(company) : createNewCompanyDraft());
    }
  }, [company, mode, open]);
  const setField = <Key extends keyof CompanyDraft>(key: Key, value: CompanyDraft[Key]) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };
  return { draft, dirty: isCompanyDraftDirty(draft, original), original, setField };
};

const CompanyDrawer: React.FC<CompanyDrawerProps> = (props) => {
  const loading = props.loading ?? false;
  const { showToast } = useAdminToast();
  const [submitting, setSubmitting] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [discardOpen, setDiscardOpen] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<CompanyDraftErrors>({});
  const draft = useCompanyDraft(props.open, props.mode, props.company);
  const visibleErrors = draft.dirty ? validateCompanyDraft(draft.draft) : {};
  const errors = { ...visibleErrors, ...fieldErrors };
  const canSubmit = !submitting && draft.dirty && Object.keys(validateCompanyDraft(draft.draft)).length === 0;
  const title = props.mode === 'create' ? 'New company' : props.company?.name ?? 'Loading company...';
  const nextStatus = props.company?.status === 'suspended' ? 'active' : 'suspended';
  const legalSummary = useSuperAdminStore((state) => state.companyLegalSummary);
  const legalSummaryLoading = useSuperAdminStore((state) => state.companyLegalSummaryLoading);
  const legalSummaryError = useSuperAdminStore((state) => state.companyLegalSummaryError);
  const legalSummaryCurrent = Boolean(
    props.company
    && legalSummary?.companyId === props.company.id
  );
  const hasRetainedEvidence = Boolean(
    legalSummaryCurrent
    && legalSummary!.packages.length > 0
  );
  const legalRetentionUnknown = Boolean(
    props.mode === 'edit'
    && props.company
    && (legalSummaryLoading || legalSummaryError || !legalSummaryCurrent)
  );

  const closeSafely = () => {
    if (draft.dirty) {
      setDiscardOpen(true);
      return;
    }
    props.onClose();
  };

  const submit = async () => {
    if (!canSubmit || submitting) {
      return;
    }
    setSubmitting(true);
    setSaveError(null);
    setFieldErrors({});
    try {
      if (props.mode === 'create') {
        await props.onCreate(createCompanyCreateRequest(draft.draft));
      } else if (props.company) {
        const patch = createCompanyPatchRequest(draft.draft, draft.original);
        if (patch) {
          await props.onSave(props.company.id, patch);
        }
      }
      showToast('Company saved.');
      props.onClose();
    } catch (error) {
      const failure = getAdminApiError(error, 'company_save_failed');
      setFieldErrors(fieldError(error));
      setSaveError(`${failure.message} (${failure.code})`);
    } finally {
      setSubmitting(false);
    }
  };

  const changeStatus = async () => {
    if (!props.company || draft.dirty || submitting) {
      return;
    }
    setSubmitting(true);
    try {
      await props.onChangeStatus(props.company.id, nextStatus);
      showToast(nextStatus === 'suspended' ? 'Company suspended.' : 'Company reactivated.');
    } catch (error) {
      const failure = getAdminApiError(error, 'company_status_change_failed');
      setSaveError(`${failure.message} (${failure.code})`);
      showToast(`Company status change failed (${failure.code}).`, { tone: 'critical' });
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!props.company || submitting || hasRetainedEvidence || legalRetentionUnknown) {
      return;
    }
    setSubmitting(true);
    try {
      await props.onDelete(props.company.id, {
        confirmCascade: true,
        expectedCounts: props.company.deletePreview,
      });
      showToast('Company deleted.');
      props.onClose();
    } catch (error) {
      const failure = getAdminApiError(error, 'company_delete_failed');
      setSaveError(`Delete blocked (${failure.code}). Details were reloaded; confirm again if counts still match.`);
      setDeleteOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <EntityDrawer
        open={props.open}
        title={title}
        ariaLabel={title}
        eyebrow={<StatusPill tone={statusTone(draft.draft.status)}>{titleCase(draft.draft.status)}</StatusPill>}
        actions={props.mode === 'edit' && props.company && !loading ? (
          <>
            <AdminButton disabled={submitting} onClick={() => navigator.clipboard?.writeText(props.company!.contactEmail)}>Copy contact</AdminButton>
            <AdminButton disabled={submitting} aria-disabled={draft.dirty || submitting} onClick={() => void changeStatus()}>
              {nextStatus === 'suspended' ? 'Suspend' : 'Reactivate'}
            </AdminButton>
          </>
        ) : undefined}
        footer={(
          <>
            {props.mode === 'edit' && <AdminButton variant="danger" disabled={submitting || hasRetainedEvidence || legalRetentionUnknown} onClick={() => setDeleteOpen(true)}>Delete company</AdminButton>}
            <span className="admin-company-footer-spacer" />
            <AdminButton variant="ghost" disabled={submitting} onClick={closeSafely}>Cancel</AdminButton>
            <AdminButton variant="primary" disabled={!canSubmit} onClick={() => void submit()}>
              {submitting ? 'Saving…' : 'Save changes'}
            </AdminButton>
          </>
        )}
        dirty={draft.dirty}
        onDirtyClose={() => {
          setDiscardOpen(true);
          return false;
        }}
        onClose={closeSafely}
      >
        {loading || (props.mode === 'edit' && !props.company)
          ? <div role="status">Loading company details...</div>
          : (
            <>
              <p className="admin-company-suspension-note">
                Suspension blocks company users and licenses without deleting them.
              </p>
              {saveError && <div className="admin-company-form-error" role="alert">{saveError}</div>}
              <CompanyForm
                createdAt={props.company?.createdAt}
                disabled={submitting}
                draft={draft.draft}
                errors={errors}
                setField={draft.setField}
              />
              {props.company && <NestedDetail company={props.company} />}
              {props.company && <CompanyLegalSummary companyId={props.company.id} />}
              {hasRetainedEvidence && <p className="admin-company-suspension-note">Retained legal evidence prevents hard deletion. Manage the package lifecycle in Documents.</p>}
              {!hasRetainedEvidence && legalRetentionUnknown && <p className="admin-company-suspension-note">{legalSummaryError ? 'Legal retention status is unavailable; hard deletion remains disabled.' : 'Legal retention status is loading; hard deletion remains disabled until it is verified.'}</p>}
            </>
          )}
      </EntityDrawer>
      <ConfirmDialog
        open={deleteOpen}
        title={`Delete ${props.company?.name ?? 'this company'}?`}
        description={`This deletes the company and exactly ${formatCascadeCounts(props.company?.deletePreview ?? { users: 0, licenses: 0 })}. The request includes X-Confirm-Cascade; stale counts reload and require a new confirmation.`}
        confirmLabel={submitting ? 'Deleting…' : 'Delete company'}
        confirmDisabled={submitting}
        closeOnConfirm={false}
        onConfirm={confirmDelete}
        onClose={() => setDeleteOpen(false)}
      />
      <ConfirmDialog
        open={discardOpen}
        title="Discard unsaved changes?"
        description="Your company edits will be lost."
        confirmLabel="Discard changes"
        closeOnConfirm={false}
        onConfirm={() => {
          setDiscardOpen(false);
          props.onClose();
        }}
        onClose={() => setDiscardOpen(false)}
      />
    </>
  );
};

export default CompanyDrawer;
